import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { pendoClasses } from "@app/core/thirdParty/pendo";
import { useChartHighlight } from "@app/hooks/useChartHighlight";
import { CarSwitch } from "@app/components/Switch";
import { CarTooltipBox } from "@app/components/TooltipBox";
import {
  ChartDateRangeSelector,
  SimulatedForwardProbabilitiesChartLegend,
  SimulatedForwardProbabilitiesInternalChart,
} from "@app/pages/assetAllocation/editPortfolioGroup/ChartSimulatedForwardProbabilities";
import { UseSimulatedForwardProbabilitiesAC } from "./useSimulatedForwardProbabilitiesAC";
import { CarAssetClassSelector } from "@app/components/AssetClassSelector";
import { useSimulatedForwardProbabilitiesACData } from "./useSimulatedForwardProbabilitiesACData";

export const ChartSimulatedForwardProbabilitiesAC = ({
  data,
  isWhiteContext,
}: {
  data: UseSimulatedForwardProbabilitiesAC;
  isWhiteContext?: boolean;
}) => {
  const { getChartData } = useSimulatedForwardProbabilitiesACData({
    nominalHeatmap1: data.nominalHeatmap1,
    realHeatmap1: data.realHeatmap1,
    simulationSuffix1: data.simulationSuffix1,
    nominalHeatmap2: data.nominalHeatmap2,
    realHeatmap2: data.realHeatmap2,
    simulationSuffix2: data.simulationSuffix2,
    isAdvanced: data.isAdvanced,
    isReal: data.isReal,
    chartDateRange1: data.chartDateRange1,
    chartDateRange2: data.chartDateRange2,
  });

  const { data1Id, onData1IdChange, data2Id, onData2IdChange } =
    data.chartDataSelector;

  const {
    label1,
    label1Downside,
    datasets1,
    label2,
    label2Downside,
    datasets2,
  } = getChartData({
    data1Id,
    data2Id,
  });

  const chartHighlight = useChartHighlight();

  // const excelExport = useExcelExport();

  // const handleExport = () => {
  //   if (!selectedItem1 || !selectedItem2) {
  //     return;
  //   }

  //   const createTable = (
  //     chartItem: CarPortfolioHeatmapChartItem
  //   ): ExcelTable => ({
  //     columns: [
  //       {
  //         format: ExcelDataFormat.general,
  //         label: "percentile",
  //       },
  //       ...(chartItem.datasets[0]?.x.map<ExcelColumn>((x) => ({
  //         format: ExcelDataFormat.general,
  //         label: x.toString(),
  //       })) ?? []),
  //     ],
  //     name: chartItem.fullLabel.replace(/[\W]+/g, "_").slice(0, 31),
  //     rows: chartItem.datasets.map((ds) => [ds.label, ...ds.y]),
  //   });

  //   excelExport.exportAllExcel({
  //     filePrefix: "Simulated_Forward_Probabilities",
  //     tables: [createTable(selectedItem1), createTable(selectedItem2)],
  //   });
  // };

  const isTwoPanel = data.isAdvanced || !data.isSameJob;

  const { min, max } = useMemo(() => {
    const allDatasets = [...(datasets1 ?? [])];
    if (isTwoPanel) {
      allDatasets.push(...(datasets2 ?? []));
    }
    const allValues = allDatasets.flatMap((d) => d.y);

    return {
      min: Math.min(...allValues),
      max: Math.max(...allValues),
    };
  }, [datasets1, datasets2, isTwoPanel]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
      <Box sx={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="par01Regular">Simple</Typography>
          <CarSwitch
            size="small"
            checked={data.isAdvanced}
            onChange={(e, value) => data.setIsAdvanced(value)}
          />
          <Typography variant="par01Regular">Advanced</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="par01Regular">Real</Typography>
          <CarSwitch
            size="small"
            checked={!data.isReal}
            onChange={(e, value) => data.setIsReal(!value)}
          />
          <Typography variant="par01Regular">Nominal</Typography>
          <CarTooltipBox
            sx={{ ml: -0.5 }}
            className={
              pendoClasses.assetClassAnalysisSimulatedForwardProbabilitiesRealNominal
            }
          />
        </Box>
        <SimulatedForwardProbabilitiesChartLegend
          sx={{ ml: "auto" }}
          datasets={datasets1 ?? []}
          chartHighlight={chartHighlight}
          isWhiteContext={isWhiteContext}
        />
      </Box>
      {datasets1 && datasets2 && (
        <Box
          sx={{
            display: "grid",
            gap: 6,
            gridTemplateColumns: isTwoPanel ? "1fr 1fr" : "1fr",
            borderStyle: "solid",
            borderColor: "gray3",
            borderWidth: data.isAdvanced ? 0 : 1,
            backgroundColor: data.isAdvanced
              ? undefined
              : isWhiteContext
                ? "gray1"
                : "white",
          }}
        >
          <Box
            sx={{
              borderStyle: "solid",
              borderColor: "gray3",
              borderWidth: data.isAdvanced ? 1 : 0,
              backgroundColor: isWhiteContext ? "gray1" : "white",
              py: 3,
              px: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                columnGap: 3,
              }}
            >
              <CarAssetClassSelector
                sx={{
                  width: 250,
                  ".MuiInputBase-root": {
                    backgroundColor: isWhiteContext ? "white" : "gray1",
                  },
                }}
                label="Asset Class"
                value={data1Id}
                onChange={onData1IdChange}
                valueField="level4Id"
                items={data.assetClasses1.items}
              />
              <ChartDateRangeSelector
                sx={{
                  width: 100,
                  ".MuiInputBase-root": {
                    backgroundColor: isWhiteContext ? "white" : "gray1",
                  },
                }}
                label={
                  <>
                    Time Frame
                    <CarTooltipBox
                      sx={{ display: "inline-block", ml: 0.5, mb: -0.5 }}
                      className={pendoClasses.editPortfolioAnalyzeTimeFrame}
                    />
                  </>
                }
                value={data.chartDateRange1}
                onChange={data.setChartDateRange1}
              />
            </Box>
            <SimulatedForwardProbabilitiesInternalChart
              sx={{ mt: 3 }}
              label={label1}
              datasets={datasets1}
              chartHighlight={chartHighlight}
              min={min}
              max={max}
            />
            <SimulatedForwardProbabilitiesInternalChart
              sx={{ mt: 4 }}
              label={label1Downside}
              datasets={datasets1}
              chartHighlight={chartHighlight}
              isDownside
              min={min}
              max={max}
            />
          </Box>
          {isTwoPanel && (
            <Box
              sx={{
                borderStyle: "solid",
                borderColor: "gray3",
                borderWidth: data.isAdvanced ? 1 : 0,
                ml: data.isAdvanced ? 0 : "1px",
                backgroundColor: isWhiteContext ? "gray1" : "white",
                py: 3,
                px: 4,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  columnGap: 3,
                  height: 60,
                }}
              >
                {data.isAdvanced && (
                  <>
                    <CarAssetClassSelector
                      sx={{
                        width: 250,
                        ".MuiInputBase-root": {
                          backgroundColor: isWhiteContext ? "white" : "gray1",
                        },
                      }}
                      label="Asset Class"
                      value={data2Id}
                      onChange={onData2IdChange}
                      valueField="level4Id"
                      items={data.assetClasses2.items}
                    />
                    <ChartDateRangeSelector
                      sx={{
                        width: 100,
                        ".MuiInputBase-root": {
                          backgroundColor: isWhiteContext ? "white" : "gray1",
                        },
                      }}
                      label={
                        <>
                          Time Frame
                          <CarTooltipBox
                            sx={{ display: "inline-block", ml: 0.5, mb: -0.5 }}
                            className={
                              pendoClasses.editPortfolioAnalyzeTimeFrame
                            }
                          />
                        </>
                      }
                      value={data.chartDateRange2}
                      onChange={data.setChartDateRange2}
                    />
                  </>
                )}
              </Box>
              <SimulatedForwardProbabilitiesInternalChart
                sx={{ mt: 3 }}
                label={label2}
                datasets={datasets2}
                chartHighlight={chartHighlight}
                min={min}
                max={max}
              />
              <SimulatedForwardProbabilitiesInternalChart
                sx={{ mt: 4 }}
                label={label2Downside}
                datasets={datasets2}
                chartHighlight={chartHighlight}
                isDownside
                min={min}
                max={max}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
